import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/founder-calendar.css';
import { toneForKey } from './founderCalendar/chipColors';
import { buildEventsByDay } from './founderCalendar/dayEvents';
import CalendarToolbar from './founderCalendar/CalendarToolbar';
import CalendarGrid from './founderCalendar/CalendarGrid';
import MultiFilter, { chooseAllSelection, resetSelection, selectionLabel, toggleSelection } from './founderCalendar/MultiFilter';

const PALETTE = ['#37b24d', '#339af0', '#e64980', '#845ef7', '#15aabf', '#f59f00', '#f06595', '#20c997'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const STAGES = { todo: 'To Do', progress: 'In Progress', review: 'In Review', done: 'Done', hold: 'On Hold' };
const PRIORITY = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };
const LEAVE_APPROVAL = { approved: 'Approved', pending: 'Pending' };

function leaveApproval(leave) {
    return String(leave?.status || 'approved').toLowerCase() === 'pending' ? 'pending' : 'approved';
}

function isCurrentLeave(leave, today) {
    return leaveStatus(leave, today) !== 'historic';
}

function colorFor(id) {
    const text = String(id || '');
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) hash = (hash * 33 + text.charCodeAt(i)) >>> 0;
    return PALETTE[hash % PALETTE.length];
}

function withColor(person) {
    if (!person) return null;
    return { ...person, color: person.color || colorFor(person.id) };
}

function paintTask(task) {
    const members = (task.members || []).map(withColor);
    return {
        ...task,
        person: withColor(task.person) || members[0] || null,
        members,
        project: { ...task.project, color: colorFor(task.project?.id) },
    };
}

function paintLeave(leave) {
    return { ...leave, person: withColor(leave.person) };
}

function parseISO(value) {
    const [year, month, day] = String(value).split('-').map(Number);
    return new Date(year, month - 1, day);
}

function addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function startOfWeek(date) {
    return addDays(date, -date.getDay());
}

function startOfToday() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function ymd(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function esc(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[char]));
}

function shortDate(date) {
    return `${date.getDate()} ${MON[date.getMonth()]}`;
}

function rangeLabel(start, end) {
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${start.getDate()}–${end.getDate()} ${MON[start.getMonth()]}`;
    }
    return `${shortDate(start)} – ${shortDate(end)}`;
}

function isOverdue(task, today) {
    if (task.stage === 'done') return false;
    if (task.missed) return true;
    if (!task.due) return false;
    return parseISO(task.due) < today;
}

function leaveStatus(leave, today) {
    const start = parseISO(leave.start);
    const end = parseISO(leave.end);
    if (end < today) return 'historic';
    if (start > today) return 'upcoming';
    return 'ongoing';
}

function viewBounds(cursor, mode) {
    if (mode === 'week') {
        const start = startOfWeek(cursor);
        return { start, end: addDays(start, 6) };
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const start = startOfWeek(first);
    return { start, end: addDays(startOfWeek(last), 6) };
}

function weeksForView(cursor, mode) {
    if (mode === 'week') return [startOfWeek(cursor)];
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    let cursorDay = startOfWeek(first);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const weeks = [];
    while (cursorDay <= last && weeks.length < 6) {
        weeks.push(cursorDay);
        cursorDay = addDays(cursorDay, 7);
    }
    return weeks;
}

function memberIds(task) {
    return (task.members && task.members.length ? task.members : (task.person ? [task.person] : [])).map((person) => person.id);
}

function intersects(owned, selected) {
    if (!owned || !selected || !owned.size) return false;
    for (const id of owned) {
        if (selected.has(id)) return true;
    }
    return false;
}

function taskSearchText(task) {
    const people = task.members && task.members.length ? task.members : (task.person ? [task.person] : []);
    const parts = [
        task.title,
        task.project?.name,
        task.project?.brand,
        task.stage,
        STAGES[task.stage],
        task.priority,
        PRIORITY[task.priority],
        task.start,
        task.end,
        task.due,
    ];
    people.forEach((person) => {
        if (!person) return;
        parts.push(person.name, person.initials);
    });
    if (!people.length) parts.push('unassigned');
    return parts.filter(Boolean).join('\n').toLowerCase();
}

function leaveSearchText(leave, links) {
    const approval = leaveApproval(leave);
    const parts = [
        leave.person?.name,
        leave.person?.initials,
        leave.type,
        approval,
        LEAVE_APPROVAL[approval],
        'leave',
        leave.start,
        leave.end,
    ];
    if (links) links.text.forEach((bit) => parts.push(bit));
    return parts.filter(Boolean).join('\n').toLowerCase();
}

function taskPasses(entry, filters, today) {
    const { task, members } = entry;
    if (filters.peopleScope === 'none') return false;
    if (filters.peopleScope === 'some' && !intersects(members, filters.peopleSet)) return false;
    if (filters.projectScope === 'none') return false;
    if (filters.projectScope === 'some' && !filters.projectSet.has(task.project?.id)) return false;
    if (filters.brandScope === 'none') return false;
    if (filters.brandScope === 'some' && !filters.brandSet.has(task.project?.brand)) return false;
    if (filters.statusScope === 'none') return false;
    if (filters.statusScope === 'some' && !filters.statusSet.has(task.stage)) return false;
    if (filters.priorityScope === 'none') return false;
    if (filters.priorityScope === 'some' && !filters.prioritySet.has(task.priority)) return false;
    if (filters.overdueOnly && !isOverdue(task, today)) return false;
    if (filters.search && entry.text.indexOf(filters.search) === -1) return false;
    return true;
}

function leavePasses(entry, filters) {
    if (filters.peopleScope === 'none') return false;
    if (filters.peopleScope === 'some' && !filters.peopleSet.has(entry.personId)) return false;
    if (filters.projectScope === 'none') return false;
    if (filters.projectScope === 'some' && !intersects(entry.projects, filters.projectSet)) return false;
    if (filters.brandScope === 'none') return false;
    if (filters.brandScope === 'some' && !intersects(entry.brands, filters.brandSet)) return false;
    if (filters.search && entry.text.indexOf(filters.search) === -1) return false;
    return true;
}

function leavesInRange(leaves, rangeStart, rangeEnd) {
    return leaves.some((leave) => parseISO(leave.start) <= rangeEnd && parseISO(leave.end) >= rangeStart);
}

function historicLeavesHtml(leaves, today, selectedLeave) {
    const historic = leaves
        .filter((leave) => leaveStatus(leave, today) === 'historic')
        .sort((a, b) => parseISO(b.end) - parseISO(a.end) || parseISO(b.start) - parseISO(a.start));
    if (!historic.length) return '<div class="side-empty">No historic leave in this window.</div>';
    return historic.map((leave) => {
        const start = parseISO(leave.start);
        const end = parseISO(leave.end);
        const dates = sameDay(start, end) ? shortDate(start) : rangeLabel(start, end);
        const approval = leaveApproval(leave);
        const cls = `leave-row is-historic is-${approval}${selectedLeave === leave.id ? ' is-selected' : ''}`;
        return `<button type="button" class="${cls}" data-leave="${esc(leave.id)}">` +
            `<span class="av" style="background:${leave.person.color}">${esc(leave.person.initials)}</span>` +
            `<span><strong>${esc(leave.person.name)}</strong><small>${esc(leave.type)} · ${esc(dates)}</small></span>` +
            `<span class="leave-tag ${approval}">${LEAVE_APPROVAL[approval]}</span></button>`;
    }).join('');
}

function legendHtml(colorBy, projects, people) {
    let keys;
    let colorLabel;
    let intro;
    if (colorBy === 'project') {
        keys = projects.map((project) => ({ tone: toneForKey(project.id), label: project.name }));
        colorLabel = 'By project · chip color is the project';
        intro = 'One calendar of project names. Leave stays off this view.';
    } else if (colorBy === 'allup') {
        keys = projects.map((project) => ({ tone: toneForKey(project.id), label: project.name }));
        colorLabel = 'All up · chip color is the project';
        intro = 'One calendar: each project’s timeline plus current approved and pending leave.';
    } else {
        keys = people.map((person) => ({ tone: toneForKey(person.id), label: person.name }));
        colorLabel = 'By user · chip color is the person';
        intro = 'One calendar. Each chip is the time a person is assigned to a project, including current leave.';
    }
    return `<span class="key">${intro}</span>` +
        (colorBy !== 'project'
            ? '<span class="key"><i class="mini-leave"></i> Approved leave</span><span class="key"><i class="mini-leave is-pending"></i> Pending leave</span>'
            : '') +
        `<div class="swatches"><span class="key">${colorLabel}</span>` +
        keys.map((key) => `<span class="key"><i class="swatch tone-${key.tone}"></i>${esc(key.label)}</span>`).join('') +
        '</div>';
}

const FounderCalendar = () => {
    const { accessToken } = useAuth() || {};
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [directoryPeople, setDirectoryPeople] = useState([]);
    const [directoryProjects, setDirectoryProjects] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cursor, setCursor] = useState(() => startOfToday());
    const [mode, setMode] = useState('month');
    const [colorBy, setColorBy] = useState('allup');
    const [selectedPeople, setSelectedPeople] = useState([]);
    const [selectAllPeople, setSelectAllPeople] = useState(true);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [selectAllProjects, setSelectAllProjects] = useState(true);
    const [sideOpen, setSideOpen] = useState(false);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectAllBrands, setSelectAllBrands] = useState(true);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectAllStatuses, setSelectAllStatuses] = useState(true);
    const [selectedPriorities, setSelectedPriorities] = useState([]);
    const [selectAllPriorities, setSelectAllPriorities] = useState(true);
    const [overdueOnly, setOverdueOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [openFilter, setOpenFilter] = useState('');
    const [, setSelectedId] = useState(null);
    const [selectedLeave, setSelectedLeave] = useState(null);

    const bounds = useMemo(() => viewBounds(cursor, mode), [cursor, mode]);

    const load = useCallback(async (quiet = false) => {
        if (!accessToken) return;
        if (!quiet) setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${config.apiBASEURL}/TaskBoardRoutes/founder-calendar`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                params: { start_date: ymd(bounds.start), end_date: ymd(bounds.end) },
            });
            setTasks((response.data?.tasks || []).map(paintTask));
            setLeaves((response.data?.leaves || []).map(paintLeave));
            setDirectoryPeople((response.data?.people || []).map(withColor));
            setDirectoryProjects((response.data?.projects || []).map((item) => ({
                ...item,
                color: colorFor(item.id),
            })));
            setTruncated(Boolean(response.data?.truncated));
        } catch (err) {
            const statusCode = err.response?.status;
            setError(statusCode === 403
                ? 'You do not have access to the Founder Calendar.'
                : (err.response?.data?.message || 'Could not load the Founder Calendar'));
        } finally {
            setLoading(false);
        }
    }, [accessToken, bounds.start, bounds.end]);

    useEffect(() => { load(false); }, [load]);

    useEffect(() => {
        if (!openFilter) return undefined;
        const close = (event) => {
            if (!event.target.closest('.multi-filter')) setOpenFilter('');
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [openFilter]);

    const today = useMemo(() => startOfToday(), []);

    const people = useMemo(() => {
        const map = new Map();
        tasks.forEach((task) => (task.members || []).forEach((person) => { if (person) map.set(person.id, person); }));
        leaves.forEach((leave) => { if (leave.person) map.set(leave.person.id, leave.person); });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [tasks, leaves]);

    const projects = useMemo(() => {
        const map = new Map();
        tasks.forEach((task) => { if (task.project) map.set(task.project.id, task.project); });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [tasks]);

    const peopleOptions = useMemo(() => {
        const map = new Map();
        directoryPeople.forEach((person) => { if (person) map.set(person.id, person); });
        people.forEach((person) => { if (person && !map.has(person.id)) map.set(person.id, person); });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [directoryPeople, people]);

    const projectOptions = useMemo(() => {
        const map = new Map();
        directoryProjects.forEach((item) => { if (item) map.set(item.id, item); });
        projects.forEach((item) => { if (item && !map.has(item.id)) map.set(item.id, item); });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [directoryProjects, projects]);

    const brands = useMemo(() => {
        const list = [];
        projectOptions.forEach((item) => { if (item.brand && !list.includes(item.brand)) list.push(item.brand); });
        return list.sort((a, b) => a.localeCompare(b));
    }, [projectOptions]);

    const peopleScope = selectAllPeople ? 'all' : (selectedPeople.length ? 'some' : 'none');
    const projectScope = selectAllProjects ? 'all' : (selectedProjects.length ? 'some' : 'none');
    const brandScope = selectAllBrands ? 'all' : (selectedBrands.length ? 'some' : 'none');
    const statusScope = selectAllStatuses ? 'all' : (selectedStatuses.length ? 'some' : 'none');
    const priorityScope = selectAllPriorities ? 'all' : (selectedPriorities.length ? 'some' : 'none');

    const filters = useMemo(() => ({
        peopleScope,
        peopleSet: peopleScope === 'some' ? new Set(selectedPeople) : null,
        projectScope,
        projectSet: projectScope === 'some' ? new Set(selectedProjects) : null,
        brandScope,
        brandSet: brandScope === 'some' ? new Set(selectedBrands) : null,
        statusScope,
        statusSet: statusScope === 'some' ? new Set(selectedStatuses) : null,
        priorityScope,
        prioritySet: priorityScope === 'some' ? new Set(selectedPriorities) : null,
        overdueOnly,
        search: search.trim().toLowerCase(),
    }), [
        peopleScope, selectedPeople, projectScope, selectedProjects, brandScope, selectedBrands,
        statusScope, selectedStatuses, priorityScope, selectedPriorities, overdueOnly, search,
    ]);

    const taskEntries = useMemo(() => tasks.map((task) => ({
        task,
        members: new Set(memberIds(task)),
        text: taskSearchText(task),
    })), [tasks]);

    const personLinks = useMemo(() => {
        const map = new Map();
        tasks.forEach((task) => {
            const projectId = task.project?.id;
            const projectName = task.project?.name || '';
            const brandName = task.project?.brand || '';
            memberIds(task).forEach((id) => {
                let row = map.get(id);
                if (!row) {
                    row = { projects: new Set(), brands: new Set(), text: new Set() };
                    map.set(id, row);
                }
                if (projectId) row.projects.add(projectId);
                if (projectName) row.text.add(projectName);
                if (brandName) {
                    row.brands.add(brandName);
                    row.text.add(brandName);
                }
            });
        });
        return map;
    }, [tasks]);

    const leaveEntries = useMemo(() => leaves.map((leave) => {
        const links = personLinks.get(leave.person?.id);
        return {
            leave,
            personId: leave.person?.id,
            projects: links ? links.projects : null,
            brands: links ? links.brands : null,
            text: leaveSearchText(leave, links),
        };
    }), [leaves, personLinks]);

    const shown = useMemo(
        () => taskEntries.filter((entry) => taskPasses(entry, filters, today)).map((entry) => entry.task),
        [taskEntries, filters, today]
    );

    const matchedLeaves = useMemo(
        () => leaveEntries.filter((entry) => leavePasses(entry, filters)).map((entry) => entry.leave),
        [leaveEntries, filters]
    );

    const currentLeaves = useMemo(
        () => matchedLeaves.filter((leave) => isCurrentLeave(leave, today)),
        [matchedLeaves, today]
    );

    const historicLeaves = useMemo(
        () => matchedLeaves.filter((leave) => leaveStatus(leave, today) === 'historic'),
        [matchedLeaves, today]
    );

    const weeks = useMemo(() => weeksForView(cursor, mode), [cursor, mode]);

    const eventsByDay = useMemo(
        () => buildEventsByDay(weeks, shown, matchedLeaves, colorBy, selectedLeave),
        [weeks, shown, matchedLeaves, colorBy, selectedLeave]
    );

    const emptyText = useMemo(() => {
        if (!weeks.length) return '';
        const rangeStart = weeks[0];
        const rangeEnd = addDays(weeks[weeks.length - 1], 6);
        const scheduledInRange = shown.some((task) => (
            task.start && task.end && parseISO(task.start) <= rangeEnd && parseISO(task.end) >= rangeStart
        ));
        const leaveInRange = colorBy !== 'project' && leavesInRange(matchedLeaves, rangeStart, rangeEnd);
        if (scheduledInRange || leaveInRange) return '';
        const selected = projectScope === 'some'
            ? projectOptions.filter((item) => selectedProjects.includes(item.id))
            : [];
        const selectedHasCards = selected.some((item) => (
            tasks.some((task) => task.project && task.project.id === item.id)
        ));
        if (selected.length && !selectedHasCards) return 'No tasks added yet.';
        if (shown.some((task) => task.start || task.due)) return 'Nothing in this range. Use the arrows to move to another month.';
        return 'No tasks match these filters.';
    }, [weeks, shown, colorBy, matchedLeaves, projectScope, selectedProjects, projectOptions, tasks]);

    const historicHtml = useMemo(
        () => historicLeavesHtml(matchedLeaves, today, selectedLeave),
        [matchedLeaves, today, selectedLeave]
    );
    const legend = useMemo(() => legendHtml(colorBy, projects, people), [colorBy, projects, people]);

    const title = mode === 'week'
        ? (bounds.start.getMonth() === bounds.end.getMonth()
            ? `${bounds.start.getDate()} – ${bounds.end.getDate()} ${MONTHS[bounds.start.getMonth()]} ${bounds.start.getFullYear()}`
            : `${shortDate(bounds.start)} – ${shortDate(bounds.end)} ${bounds.end.getFullYear()}`)
        : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

    const peopleChoices = useMemo(() => peopleOptions.map((person) => ({
        id: person.id,
        label: person.name,
        color: person.color,
        mark: person.initials,
    })), [peopleOptions]);

    const projectChoices = useMemo(() => projectOptions.map((item) => ({
        id: item.id,
        label: item.name,
        color: item.color,
        mark: String(item.name || '?').charAt(0).toUpperCase(),
    })), [projectOptions]);

    const brandChoices = useMemo(() => brands.map((name) => ({ id: name, label: name })), [brands]);
    const statusChoices = useMemo(() => Object.keys(STAGES).map((id) => ({ id, label: STAGES[id] })), []);
    const priorityChoices = useMemo(() => Object.keys(PRIORITY).map((id) => ({ id, label: PRIORITY[id] })), []);

    const filtersActive = !selectAllPeople || !selectAllProjects || !selectAllBrands || !selectAllStatuses || !selectAllPriorities || overdueOnly || search;
    const scheduledCount = shown.filter((task) => task.start || task.due).length;
    const currentLeaveCount = currentLeaves.length;
    const historicCount = historicLeaves.length;

    const shift = (direction) => {
        setCursor((current) => {
            const next = new Date(current);
            if (mode === 'week') next.setDate(next.getDate() + direction * 7);
            else next.setMonth(next.getMonth() + direction);
            return next;
        });
    };

    const clearFilters = () => {
        setSelectAllPeople(true);
        setSelectedPeople([]);
        setSelectAllProjects(true);
        setSelectedProjects([]);
        setSelectAllBrands(true);
        setSelectedBrands([]);
        setSelectAllStatuses(true);
        setSelectedStatuses([]);
        setSelectAllPriorities(true);
        setSelectedPriorities([]);
        setOverdueOnly(false);
        setSearch('');
        setOpenFilter('');
    };

    const openMenu = (name) => setOpenFilter((current) => (current === name ? '' : name));

    const openBoard = (boardId) => {
        if (!boardId) return;
        navigate(`/Graphe/dashboard/kanbanboard/${boardId}`);
    };

    const goToLeave = useCallback((leave) => {
        if (!leave?.start) return;
        const start = parseISO(leave.start);
        setCursor(mode === 'week' ? startOfWeek(start) : new Date(start.getFullYear(), start.getMonth(), 1));
    }, [mode]);

    const openEvent = (event) => {
        if (!event) return;
        if (event.kind === 'leave') {
            const id = event.leaveId;
            const leave = matchedLeaves.find((item) => item.id === id) || leaves.find((item) => item.id === id);
            setSelectedId(null);
            if (selectedLeave === id) {
                setSelectedLeave(null);
                return;
            }
            setSelectedLeave(id);
            if (leave) goToLeave(leave);
            return;
        }
        openBoard(event.boardId);
    };

    const onSurfaceClick = (event) => {
        const leaveNode = event.target.closest('[data-leave]');
        if (!leaveNode) return;
        const id = leaveNode.getAttribute('data-leave');
        const leave = matchedLeaves.find((item) => item.id === id) || leaves.find((item) => item.id === id);
        setSelectedId(null);
        if (selectedLeave === id) {
            setSelectedLeave(null);
            return;
        }
        setSelectedLeave(id);
        if (leave) goToLeave(leave);
    };

    const goToday = () => setCursor(startOfToday());

    return (
        <div className="body_content founder-cal">
            <div className="page-head">
                <div>
                    <div className="crumbs">Projects / <b>Founder Calendar</b></div>
                    <h1>Founder Calendar</h1>
                </div>
            </div>

            {truncated && (
                <p className="trunc-note">Showing the first 500 dated tasks in this range.</p>
            )}
            {error && <p className="cal-error">{error}</p>}
            {loading && <p className="loading-line">Loading calendar…</p>}

            <section className="filters card" aria-label="Filters">
                <MultiFilter
                    label="People"
                    open={openFilter === 'people'}
                    onOpen={() => openMenu('people')}
                    buttonLabel={selectionLabel(selectAllPeople, selectedPeople, peopleChoices, '1 person', 'people')}
                    allSelected={selectAllPeople}
                    onChooseAll={() => chooseAllSelection(setSelectAllPeople, setSelectedPeople)}
                    options={peopleChoices}
                    selectedIds={selectedPeople}
                    onToggle={(id) => toggleSelection(id, selectAllPeople, setSelectAllPeople, selectedPeople, setSelectedPeople, peopleChoices)}
                    onReset={() => resetSelection(setSelectAllPeople, setSelectedPeople)}
                    emptyText="No people found."
                />
                <MultiFilter
                    label="Project"
                    open={openFilter === 'project'}
                    onOpen={() => openMenu('project')}
                    buttonLabel={selectionLabel(selectAllProjects, selectedProjects, projectChoices, '1 project', 'projects')}
                    allSelected={selectAllProjects}
                    onChooseAll={() => chooseAllSelection(setSelectAllProjects, setSelectedProjects)}
                    options={projectChoices}
                    selectedIds={selectedProjects}
                    onToggle={(id) => toggleSelection(id, selectAllProjects, setSelectAllProjects, selectedProjects, setSelectedProjects, projectChoices)}
                    onReset={() => resetSelection(setSelectAllProjects, setSelectedProjects)}
                    emptyText="No projects found."
                />
                <MultiFilter
                    label="Brand"
                    open={openFilter === 'brand'}
                    onOpen={() => openMenu('brand')}
                    buttonLabel={selectionLabel(selectAllBrands, selectedBrands, brandChoices, '1 brand', 'brands')}
                    allSelected={selectAllBrands}
                    onChooseAll={() => chooseAllSelection(setSelectAllBrands, setSelectedBrands)}
                    options={brandChoices}
                    selectedIds={selectedBrands}
                    onToggle={(id) => toggleSelection(id, selectAllBrands, setSelectAllBrands, selectedBrands, setSelectedBrands, brandChoices)}
                    onReset={() => resetSelection(setSelectAllBrands, setSelectedBrands)}
                    emptyText="No brands found."
                />
                <MultiFilter
                    label="Status"
                    open={openFilter === 'status'}
                    onOpen={() => openMenu('status')}
                    buttonLabel={selectionLabel(selectAllStatuses, selectedStatuses, statusChoices, '1 status', 'statuses')}
                    allSelected={selectAllStatuses}
                    onChooseAll={() => chooseAllSelection(setSelectAllStatuses, setSelectedStatuses)}
                    options={statusChoices}
                    selectedIds={selectedStatuses}
                    onToggle={(id) => toggleSelection(id, selectAllStatuses, setSelectAllStatuses, selectedStatuses, setSelectedStatuses, statusChoices)}
                    onReset={() => resetSelection(setSelectAllStatuses, setSelectedStatuses)}
                    emptyText="No statuses found."
                />
                <MultiFilter
                    label="Priority"
                    open={openFilter === 'priority'}
                    onOpen={() => openMenu('priority')}
                    buttonLabel={selectionLabel(selectAllPriorities, selectedPriorities, priorityChoices, '1 priority', 'priorities')}
                    allSelected={selectAllPriorities}
                    onChooseAll={() => chooseAllSelection(setSelectAllPriorities, setSelectedPriorities)}
                    options={priorityChoices}
                    selectedIds={selectedPriorities}
                    onToggle={(id) => toggleSelection(id, selectAllPriorities, setSelectAllPriorities, selectedPriorities, setSelectedPriorities, priorityChoices)}
                    onReset={() => resetSelection(setSelectAllPriorities, setSelectedPriorities)}
                    emptyText="No priorities found."
                />
                <label className={`switch${overdueOnly ? ' is-on' : ''}`}>
                    <input
                        type="checkbox"
                        checked={overdueOnly}
                        onChange={(event) => setOverdueOnly(event.target.checked)}
                    />
                    <span className="track" />
                    Overdue only
                </label>
                {filtersActive ? <button type="button" className="text-btn" onClick={clearFilters}>Clear</button> : null}
                <span className="visible-count">{scheduledCount} on calendar · {currentLeaveCount} on leave</span>
                <div className="search-row">
                    <input
                        className="search"
                        type="search"
                        placeholder="Search the calendar"
                        autoComplete="off"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
            </section>

            <div className={`workspace${sideOpen ? '' : ' side-collapsed'}`}>
                <section className="card cal-card">
                    <CalendarToolbar title={title} mode={mode} onToday={goToday} onShift={shift}>
                        <button
                            type="button"
                            className={`historic-btn${sideOpen ? ' is-on' : ''}`}
                            aria-expanded={sideOpen}
                            aria-controls="historic-leave-panel"
                            onClick={() => setSideOpen((open) => !open)}
                        >
                            Historic leave
                            <span className="count-pill">{historicCount}</span>
                        </button>
                        <div className="seg solid" role="group" aria-label="Color coding">
                            <button type="button" className={colorBy === 'allup' ? 'is-on' : ''} onClick={() => setColorBy('allup')}>All up</button>
                            <button type="button" className={colorBy === 'project' ? 'is-on' : ''} onClick={() => setColorBy('project')}>By project</button>
                            <button type="button" className={colorBy === 'assignee' ? 'is-on' : ''} onClick={() => setColorBy('assignee')}>By user</button>
                        </div>
                        <div className="seg" role="group" aria-label="Calendar range">
                            <button type="button" className={mode === 'month' ? 'is-on' : ''} onClick={() => setMode('month')}>Month</button>
                            <button type="button" className={mode === 'week' ? 'is-on' : ''} onClick={() => setMode('week')}>Week</button>
                        </div>
                    </CalendarToolbar>
                    <CalendarGrid
                        weeks={weeks}
                        mode={mode}
                        viewYear={cursor.getFullYear()}
                        viewMonth={cursor.getMonth()}
                        today={today}
                        eventsByDay={eventsByDay}
                        onOpen={openEvent}
                    />
                    {emptyText ? <p className="empty-cal">{emptyText}</p> : null}
                    <div className="legend" dangerouslySetInnerHTML={{ __html: legend }} />
                </section>
                <aside
                    id="historic-leave-panel"
                    className={`card side-panel${sideOpen ? '' : ' is-collapsed'}`}
                    aria-label="Historic leave"
                    aria-hidden={!sideOpen}
                >
                    {sideOpen && (
                        <>
                            <div className="side-head">
                                <button
                                    type="button"
                                    className="side-collapse"
                                    aria-label="Collapse historic leave"
                                    onClick={() => setSideOpen(false)}
                                >
                                    <span className="leave-chevron is-open" aria-hidden="true" />
                                    <h3>Historic leave</h3>
                                </button>
                                <span className="count-pill">{historicCount}</span>
                            </div>
                            <p className="side-note">Past leave (approved & pending)</p>
                            <div
                                className="side-list"
                                onClick={onSurfaceClick}
                                dangerouslySetInnerHTML={{ __html: historicHtml }}
                            />
                        </>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default FounderCalendar;
